import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLexware } from '../../context/LexwareContext';
import { format, isBefore, isSameDay, isAfter } from 'date-fns';

const { FiCalendar, FiClock, FiPhone, FiMail, FiAlertCircle, FiCheckCircle } = FiIcons;

function TodaysTasks({ onTaskClick }) {
  const { data } = useLexware();
  
  const { todayAndOverdueTasks, futureTasks, totalCount } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const allPendingTasks = (data.tasks || [])
      .filter(task => task.status === 'PENDING')
      .map(task => {
        const customer = data.customers?.find(c => c.id === task.customerId);
        const dueDate = new Date(task.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        return { ...task, customer, dueDateNormalized: dueDate };
      });

    // Aufteilen in heute/überfällig und zukünftig
    const todayAndOverdue = allPendingTasks
      .filter(task => isSameDay(task.dueDateNormalized, today) || isBefore(task.dueDateNormalized, today))
      .sort((a, b) => a.dueDateNormalized - b.dueDateNormalized);

    const future = allPendingTasks
      .filter(task => isAfter(task.dueDateNormalized, today))
      .sort((a, b) => a.dueDateNormalized - b.dueDateNormalized);

    return {
      todayAndOverdueTasks: todayAndOverdue,
      futureTasks: future,
      totalCount: allPendingTasks.length
    };
  }, [data.tasks, data.customers]);

  const getTaskIcon = (type) => {
    switch (type) {
      case 'CALL': return FiPhone;
      case 'EMAIL': return FiMail;
      default: return FiClock;
    }
  };

  const getTaskColor = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dDate = new Date(dueDate);
    dDate.setHours(0, 0, 0, 0);

    if (isBefore(dDate, today)) return 'text-red-600 bg-red-50 border-red-100';
    if (isSameDay(dDate, today)) return 'text-orange-600 bg-orange-50 border-orange-100';
    return 'text-blue-600 bg-blue-50 border-blue-100';
  };

  const renderTask = (task, index, isFuture = false) => (
    <motion.div 
      key={task.id}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={() => task.customer && onTaskClick(task.customer)}
      className={`flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 ${getTaskColor(task.dueDate)}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
          <SafeIcon icon={getTaskIcon(task.type)} className="text-lg" />
        </div>
        <div>
          <p className="font-black text-gray-900 text-sm leading-tight">{task.title}</p>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
            {task.customer?.name || task.customerId}
          </p>
        </div>
      </div>
      <div className="text-right">
        {isBefore(new Date(task.dueDate), new Date(new Date().setHours(0,0,0,0))) ? (
          <div className="flex items-center gap-1 text-[10px] font-black uppercase text-red-600">
            <SafeIcon icon={FiAlertCircle} /> Überfällig
          </div>
        ) : isSameDay(new Date(task.dueDate), new Date(new Date().setHours(0,0,0,0))) ? (
          <div className="text-[10px] font-black uppercase text-orange-600">Heute</div>
        ) : (
          <div className="text-[10px] font-black uppercase text-blue-600">
            {format(new Date(task.dueDate), 'dd.MM.yyyy')}
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col h-fit"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiCalendar} className="text-orange-500 text-xl" />
          <h3 className="text-lg font-black text-gray-900">Wiedervorlagen</h3>
        </div>
        <div className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-xs font-black">
          {totalCount}
        </div>
      </div>
      
      {/* Scrollbarer Bereich: max 10 Aufgaben sichtbar, dann Scrollbar */}
      <div className="space-y-3 overflow-y-auto max-h-[600px] pr-2 custom-scrollbar">
        {totalCount > 0 ? (
          <>
            {/* Heute & Überfällige Aufgaben */}
            {todayAndOverdueTasks.map((task, index) => renderTask(task, index, false))}
            
            {/* Zukünftige Aufgaben */}
            {futureTasks.length > 0 && todayAndOverdueTasks.length > 0 && (
              <div className="pt-4 pb-2">
                <div className="flex items-center gap-2">
                  <div className="h-px bg-gray-200 flex-1"></div>
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Geplant</span>
                  <div className="h-px bg-gray-200 flex-1"></div>
                </div>
              </div>
            )}
            {futureTasks.map((task, index) => renderTask(task, index, true))}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="w-12 h-12 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-3">
              <SafeIcon icon={FiCheckCircle} className="text-2xl" />
            </div>
            <p className="text-sm font-bold text-gray-400">Alles erledigt für heute!</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default TodaysTasks;